import { useContext, useEffect, useMemo, useRef, useState } from "react";
import styled, { css, keyframes, useTheme } from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Box,
  Flex,
  Text,
  Icon,
  useScreenResolution,
  ViewerContext,
  ModalContext,
  UpgradeSubscriptionModalType,
  useCheckIfSubscriptionFeatureActiveBroker,
} from "dash-ui";
import { DashFeaturesEnum } from "dash-common";

import { useQuickTour } from "common/components/QuickTour";
import UpgradeSubscriptionModal from "common/components/SubscriptionComponents/UpgradeSubscriptionModalV3/UpgradeSubscriptionModalV3";

import { ChecklistItemId, useChecklistState } from "./useChecklistState";

const PILL_PROGRESS_SIZE = 22;
const PILL_PROGRESS_STROKE = 2.5;
const PANEL_WIDTH = 320;

// --- DSH-857: page-aware item model -----------------------------------------
// One global checklist, page-based view (Jorden, DSH-857). Items are grouped by
// page; the current page floats to an "On this page" section, the rest sit under
// "Explore next", and plan-locked pages appear in a separate "Unlock" group
// (excluded from the X/N bar). Content never swaps by page — only reorders.

type ChecklistPageKey =
  | "properties"
  | "search"
  | "watchlist"
  | "portfolios"
  | "exchange";

type ItemAction =
  // Launches the page's DSH-856 QuickTour from step 1 (via requestTour).
  | { kind: "tour"; tourKey: string; route: string }
  // Keeps the pre-existing single-element GuideHighlight spotlight (the click
  // behaviour is ported verbatim in handleGuideClick to avoid regressions).
  | { kind: "guide" };

interface ChecklistItemConfig {
  id: ChecklistItemId;
  page: ChecklistPageKey;
  labelKey: string;
  action: ItemAction;
  // When set, the item is plan-gated: users without the feature see it in the
  // "Unlock" group (click opens the upgrade modal) instead of as a live item.
  requiredFeature?: DashFeaturesEnum;
  // Portfolios / eXchange are hidden entirely for Seeker accounts (they are
  // hard-redirected off those routes), matching the existing isSeeker filter.
  hideForSeeker?: boolean;
  upgradeType?: UpgradeSubscriptionModalType;
}

const CHECKLIST_ITEMS: ChecklistItemConfig[] = [
  {
    id: "quick-tour",
    page: "properties",
    labelKey: "quickStart.itemTour",
    action: { kind: "tour", tourKey: "properties", route: "/properties" },
  },
  {
    id: "open-development",
    page: "properties",
    labelKey: "quickStart.itemOpenDevelopment",
    action: { kind: "guide" },
  },
  {
    id: "first-search",
    page: "search",
    labelKey: "quickStart.itemSearch",
    action: { kind: "guide" },
  },
  {
    id: "try-filter",
    page: "search",
    labelKey: "quickStart.itemTryFilter",
    action: { kind: "tour", tourKey: "search", route: "/properties/search" },
  },
  {
    id: "save-property",
    page: "watchlist",
    labelKey: "quickStart.itemSaveProperty",
    action: { kind: "guide" },
  },
  {
    id: "view-watchlist",
    page: "watchlist",
    labelKey: "quickStart.itemViewWatchlist",
    action: { kind: "tour", tourKey: "watchlist", route: "/watchlist" },
  },
  {
    id: "check-portfolios",
    page: "portfolios",
    labelKey: "quickStart.itemPortfolios",
    action: { kind: "tour", tourKey: "portfolio", route: "/portfolios" },
    requiredFeature: DashFeaturesEnum.PORTFOLIO_CREATION_AND_SHARING,
    hideForSeeker: true,
    upgradeType: UpgradeSubscriptionModalType.PORTFOLIO,
  },
  {
    id: "explore-exchange",
    page: "exchange",
    labelKey: "quickStart.itemExploreExchange",
    action: { kind: "tour", tourKey: "exchange-page", route: "/exchange" },
    requiredFeature: DashFeaturesEnum.IDX_INTEGRATION,
    hideForSeeker: true,
    upgradeType: UpgradeSubscriptionModalType.IDX,
  },
];

const PAGE_PATHS: Record<ChecklistPageKey, string> = {
  properties: "/properties",
  search: "/properties/search",
  watchlist: "/watchlist",
  portfolios: "/portfolios",
  exchange: "/exchange",
};

// User-facing page labels live in the dash-ui "common" dictionary (labels.*).
const PAGE_LABEL_KEY: Record<ChecklistPageKey, string> = {
  properties: "labels.properties",
  search: "labels.search",
  watchlist: "labels.watchlist",
  portfolios: "labels.portfolios",
  exchange: "labels.exchange",
};

// EXACT route match only. Detail subroutes (/portfolios/:id, /exchange/:feedId,
// /properties/:id/preview) resolve to null → the pill shows plain "Pulse" and
// there is no "On this page" section, so a tour item click always navigates to
// the list route where a consumer wrapper is mounted (no phantom request).
const getCurrentPageKey = (pathname: string): ChecklistPageKey | null =>
  (Object.keys(PAGE_PATHS) as ChecklistPageKey[]).find(
    (key) => PAGE_PATHS[key] === pathname
  ) ?? null;

// requestTour keys → the exact route whose wrapper consumes the request. Used
// to drop a stale (unconsumed) request when the user navigates away instead.
const TOUR_ROUTE: Record<string, string> = {
  properties: "/properties",
  search: "/properties/search",
  watchlist: "/watchlist",
  portfolio: "/portfolios",
  "exchange-page": "/exchange",
};

const Wrapper = styled.div`
  position: relative;
  margin-right: 16px;
`;

const pillBump = keyframes`
  0% { transform: scale(1); }
  40% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const Pill = styled.button<{ $isOpen: boolean; $justCompleted: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px 6px 8px;
  border: 1px solid
    ${({ theme, $isOpen }) => ($isOpen ? theme.colors.gr7 : theme.colors.b30)};
  background: ${({ theme, $isOpen }) =>
    $isOpen ? theme.colors.white : theme.colors.b30};
  border-radius: 16px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme, $isOpen }) =>
    $isOpen ? theme.colors.gr1 : theme.colors.b60};
  box-shadow: ${({ $isOpen }) =>
    $isOpen ? "none" : "0 2px 8px rgba(15, 64, 122, 0.12)"};
  transition: background 0.15s ease, box-shadow 0.15s ease, color 0.15s ease,
    border-color 0.15s ease;

  ${({ $justCompleted }) =>
    $justCompleted &&
    css`
      animation: ${pillBump} 0.6s ease 2s;
    `}

  &:hover {
    background: ${({ theme, $isOpen }) =>
      $isOpen ? theme.colors.n10 : theme.colors.b20};
  }
`;

// The brand word only. Dropped on mobile to buy room — the page name next to it
// is the part DSH-857 actually needs on screen, so it is NOT inside this.
const PillEyebrow = styled.span`
  @media ${({ theme }) => theme.devices.mobile} {
    display: none;
  }
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: ${PANEL_WIDTH}px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  padding: 16px;
  z-index: 10;
  /* A phone held sideways is ~390px tall and the panel ~540px: without a cap
     its footer ("Don't show again") sat below the screen with no way to reach
     it. dvh follows the mobile browser's toolbar; vh is the fallback. */
  max-height: calc(100vh - 72px);
  max-height: calc(100dvh - 72px);
  overflow-y: auto;
  overscroll-behavior: contain;

  /* Phones and tablets also have the 56px bottom nav, which sits above the
     panel and would cover its footer. */
  @media ${({ theme }) => `${theme.devices.tablet}, ${theme.devices.mobile}`} {
    max-height: calc(100vh - 128px);
    max-height: calc(100dvh - 128px);
  }

  @media ${({ theme }) => theme.devices.mobile} {
    position: fixed;
    top: 58px;
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    width: calc(100vw - 32px);
    max-width: ${PANEL_WIDTH}px;
    max-height: calc(100vh - 130px);
    max-height: calc(100dvh - 130px);
  }
`;

const Heading = styled(Text)`
  font-family: "Work Sans Bold";
`;

const ProgressBarTrack = styled.div`
  width: 100%;
  height: 4px;
  background: ${({ theme }) => theme.colors.n20};
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => Math.round($progress * 100)}%;
  background: ${({ theme }) => theme.colors.b50};
  transition: width 0.25s ease;
`;

const CloseIconButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.n40};
  }
`;

const SectionLabel = styled(Text)`
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 11px;
  margin: 12px 2px 2px;
`;

const ItemRow = styled.button<{ $interactive: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 6px;
  background: transparent;
  border: none;
  cursor: ${({ $interactive }) => ($interactive ? "pointer" : "default")};
  text-align: left;
  border-radius: 6px;

  &:hover {
    background: ${({ theme, $interactive }) =>
      $interactive ? theme.colors.n30 : "transparent"};
  }
`;

const circlePop = keyframes`
  0% { transform: scale(1); }
  45% { transform: scale(1.2); }
  100% { transform: scale(1); }
`;

const checkIn = keyframes`
  from { transform: scale(0); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

const strikeFadeIn = keyframes`
  from { text-decoration-color: transparent; }
  to { text-decoration-color: currentColor; }
`;

const ItemCircle = styled.span<{ $completed: boolean; $animate: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: ${({ theme, $completed }) =>
    $completed ? theme.colors.b50 : "transparent"};
  border: ${({ theme, $completed }) =>
    $completed ? "none" : `1.5px solid ${theme.colors.n50}`};
  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${circlePop} 0.45s ease;
    `}
`;

const LockCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex: 0 0 auto;
`;

const CheckIconWrapper = styled.span<{ $animate: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${checkIn} 0.3s ease 0.1s backwards;
    `}
`;

const ItemLabel = styled(Text)<{ $completed: boolean; $animate: boolean }>`
  flex: 1;
  color: ${({ theme, $completed }) =>
    $completed ? theme.colors.gr5 : theme.colors.gr2};

  ${({ $completed, $animate }) =>
    $completed &&
    ($animate
      ? css`
          text-decoration: line-through transparent;
          animation: ${strikeFadeIn} 0.35s ease 0.2s forwards;
        `
      : css`
          text-decoration: line-through;
        `)}
`;

const UpgradeTag = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.b50};
  white-space: nowrap;
`;

// Completed tour items stay clickable to replay their tour (replay affordance).
const ReplayTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.b50};
  white-space: nowrap;
`;

const Footer = styled(Flex)`
  border-top: 1px solid ${({ theme }) => theme.colors.n40};
  margin-top: 8px;
  padding-top: 12px;
  align-items: center;
  justify-content: space-between;
`;

const FooterLink = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.b50};
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const slideOut = keyframes`
  from { transform: translateY(0); opacity: 1; }
  to { transform: translateY(-100%); opacity: 0; }
`;

const slideIn = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const RouletteContainer = styled.span`
  position: relative;
  display: inline-block;
  vertical-align: baseline;
`;

const SlidingInDigit = styled.span`
  display: inline-block;
  animation: ${slideIn} 0.4s ease both;
`;

const SlidingOutDigit = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  display: inline-block;
  animation: ${slideOut} 0.4s ease both;
`;

const ROULETTE_DURATION_MS = 400;

const RouletteNumber = ({ value }: { value: number }) => {
  const [displayed, setDisplayed] = useState(value);
  const [prev, setPrev] = useState<number | null>(null);

  useEffect(() => {
    if (value === displayed) return;
    setPrev(displayed);
    setDisplayed(value);
    const t = setTimeout(() => setPrev(null), ROULETTE_DURATION_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (prev !== null) {
    return (
      <RouletteContainer>
        <SlidingOutDigit>{prev}</SlidingOutDigit>
        <SlidingInDigit>{displayed}</SlidingInDigit>
      </RouletteContainer>
    );
  }
  return <>{displayed}</>;
};

const ProgressRing = ({ progress }: { progress: number }) => {
  const theme = useTheme();
  const r = (PILL_PROGRESS_SIZE - PILL_PROGRESS_STROKE) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={PILL_PROGRESS_SIZE} height={PILL_PROGRESS_SIZE}>
      <circle
        cx={PILL_PROGRESS_SIZE / 2}
        cy={PILL_PROGRESS_SIZE / 2}
        r={r}
        fill="none"
        stroke={theme.colors.n20}
        strokeWidth={PILL_PROGRESS_STROKE}
      />
      <circle
        cx={PILL_PROGRESS_SIZE / 2}
        cy={PILL_PROGRESS_SIZE / 2}
        r={r}
        fill="none"
        stroke={theme.colors.b50}
        strokeWidth={PILL_PROGRESS_STROKE}
        strokeDasharray={c}
        strokeDashoffset={c * (1 - progress)}
        strokeLinecap="round"
        transform={`rotate(-90 ${PILL_PROGRESS_SIZE / 2} ${
          PILL_PROGRESS_SIZE / 2
        })`}
        style={{ transition: "stroke-dashoffset 1s ease 1s" }}
      />
    </svg>
  );
};

const QuickStartChecklist = () => {
  const theme = useTheme();
  const { t } = useTranslation(["pages", "common"]);
  const {
    dismissed,
    isRevealed,
    isComplete,
    isRecentlyCompleted,
    anyRecentlyCompleted,
    dismiss,
    startGuide,
  } = useChecklistState();
  const navigate = useNavigate();
  const location = useLocation();
  const { requestedTourPage, requestTour } = useQuickTour();
  const {
    devices: { isMobile },
  } = useScreenResolution();
  const { account } = useContext(ViewerContext);
  const isSeeker = account?.isSeeker;
  const { toggleModal, setUpgradeSubscriptionModal, upgradeSubscriptionModal } =
    useContext(ModalContext);

  // Plan gating for the two feature-locked pages. Called once each (the hook
  // captures its feature set at call time), read with disableModal so probing
  // access never pops the upgrade modal on its own.
  const { checkFeatureAccess: checkPortfolioAccess } =
    useCheckIfSubscriptionFeatureActiveBroker([
      DashFeaturesEnum.PORTFOLIO_CREATION_AND_SHARING,
    ]);
  const { checkFeatureAccess: checkIdxAccess } =
    useCheckIfSubscriptionFeatureActiveBroker([
      DashFeaturesEnum.IDX_INTEGRATION,
    ]);
  const hasPortfolio = checkPortfolioAccess({ disableModal: true });
  const hasIdx = checkIdxAccess({ disableModal: true });

  const hasFeatureFor = (cfg: ChecklistItemConfig): boolean => {
    if (cfg.requiredFeature === DashFeaturesEnum.IDX_INTEGRATION) return hasIdx;
    if (cfg.requiredFeature === DashFeaturesEnum.PORTFOLIO_CREATION_AND_SHARING)
      return hasPortfolio;
    return true;
  };

  // Partition items into live vs locked (Option A: locked items are excluded
  // from the X/N denominator and shown in their own "Unlock" group; Seekers
  // never see the gated pages at all).
  const { activeItems, lockedItems } = useMemo(() => {
    const active: ChecklistItemConfig[] = [];
    const locked: ChecklistItemConfig[] = [];
    for (const cfg of CHECKLIST_ITEMS) {
      if (cfg.hideForSeeker && isSeeker) continue;
      if (cfg.requiredFeature && !hasFeatureFor(cfg)) {
        locked.push(cfg);
        continue;
      }
      active.push(cfg);
    }
    return { activeItems: active, lockedItems: locked };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSeeker, hasPortfolio, hasIdx]);

  const completedCount = activeItems.filter((c) => isComplete(c.id)).length;
  const totalCount = activeItems.length;

  const currentPageKey = getCurrentPageKey(location.pathname);
  const onThisPageItems = currentPageKey
    ? activeItems.filter((c) => c.page === currentPageKey)
    : [];
  const exploreNextItems = activeItems.filter(
    (c) => !onThisPageItems.includes(c)
  );
  // Portfolios and eXchange have exactly one item each, and it is the one that
  // gets locked without the plan — so on those two pages a locked user would
  // otherwise get no "On this page" section at all, which is precisely where
  // they are standing. Float the locked row up instead (still rendered locked,
  // still out of the X/N denominator) and leave the rest in "Unlock".
  const onThisPageLockedItems = currentPageKey
    ? lockedItems.filter((c) => c.page === currentPageKey)
    : [];
  const otherLockedItems = lockedItems.filter(
    (c) => !onThisPageLockedItems.includes(c)
  );
  const hasOnThisPage =
    onThisPageItems.length > 0 || onThisPageLockedItems.length > 0;
  const currentPageLabel = currentPageKey
    ? t(PAGE_LABEL_KEY[currentPageKey], { ns: "common" })
    : null;

  // Auto-expand on initial reveal (the tour just finished). See the effect
  // below for the already-mounted case.
  const [isExpanded, setIsExpanded] = useState(() => isRevealed);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Sticky once we've opened the upgrade modal. The modal below is mounted only
  // when there are locked items — but buying the plan is exactly what empties
  // that list, and the subscription arrives on a websocket push, so gating the
  // mount purely on `lockedItems.length` would rip the open dialog out of the
  // DOM mid-flow (no close transition, no success state) the instant the
  // purchase lands. Keeping it mounted for the rest of the session is enough.
  const [upgradeModalUsed, setUpgradeModalUsed] = useState(false);

  // Lag the *visible* count/progress so they update in sync with the pill bump
  // (2s after an item is marked complete).
  const [displayedCount, setDisplayedCount] = useState(completedCount);
  useEffect(() => {
    if (displayedCount === completedCount) return;
    const timer = setTimeout(() => setDisplayedCount(completedCount), 2000);
    return () => clearTimeout(timer);
  }, [completedCount, displayedCount]);
  const displayedProgress = totalCount ? displayedCount / totalCount : 0;

  useEffect(() => {
    if (isRevealed) setIsExpanded(true);
  }, [isRevealed]);

  useEffect(() => {
    if (!isExpanded) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setIsExpanded(false);
      }
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [isExpanded]);

  // Drop a tour request that was never consumed: once we're on a route that
  // isn't the requested tour's own route, no wrapper will consume it, so clear
  // it to avoid a phantom relaunch on a later visit. React 18 batches the
  // set+navigate from an item click, so the launch's own request survives to
  // its target route (pathname is already the target in that render).
  useEffect(() => {
    if (
      requestedTourPage &&
      location.pathname !== TOUR_ROUTE[requestedTourPage]
    ) {
      requestTour(null);
    }
  }, [location.pathname, requestedTourPage, requestTour]);

  if (dismissed) return null;

  const openUpgrade = (type?: UpgradeSubscriptionModalType) => {
    if (type) setUpgradeSubscriptionModal({ type });
    setUpgradeModalUsed(true);
    toggleModal(true, "UpgradeSubscriptionModal");
  };

  // The four Properties/Search micro-actions keep their pre-DSH-857 precise
  // GuideHighlight spotlight (ported verbatim). Page-level items launch their
  // page tour via requestTour instead (see handleItemClick).
  const handleGuideClick = (id: ChecklistItemId) => {
    if (id === "first-search") {
      const onSearchPage =
        location.pathname === "/properties" ||
        location.pathname.startsWith("/properties/search");
      if (!onSearchPage) navigate("/properties");
      startGuide("first-search", '[data-guide="search-bar"]', "overlay");
      return;
    }

    if (id === "open-development") {
      const onProperties = location.pathname === "/properties";
      const onSearch = location.pathname.startsWith("/properties/search");
      if (!onProperties && !onSearch) navigate("/properties/search");
      // On /properties (desktop), the carousel's leftmost visible card sits
      // half-clipped at the container's edge — skip it and highlight the
      // second card instead.
      const skipIndex = onProperties && !isMobile ? 1 : 0;
      startGuide(
        "open-development",
        '[data-guide="property-card"]',
        "overlay",
        skipIndex
      );
      return;
    }

    if (id === "save-property") {
      const onProperties = location.pathname === "/properties";
      const onSearch = location.pathname.startsWith("/properties/search");
      if (!onProperties && !onSearch) navigate("/properties");
      const skipIndex = onProperties && !isMobile ? 1 : 0;
      startGuide(
        "save-property",
        '[data-guide="property-card"]',
        "overlay",
        skipIndex
      );
    }
  };

  const handleItemClick = (cfg: ChecklistItemConfig) => {
    setIsExpanded(false);

    // Locked (plan-gated) item → open the upgrade modal, never a dead landing.
    if (cfg.requiredFeature && !hasFeatureFor(cfg)) {
      openUpgrade(cfg.upgradeType);
      return;
    }

    if (cfg.action.kind === "tour") {
      const route = cfg.action.route;
      // Exact match: on a detail subroute we navigate to the list route where
      // the tour wrapper is mounted, guaranteeing the request gets consumed.
      const onPage = location.pathname === route;
      if (!onPage) navigate(route);
      // Survives navigation: the target page's tour wrapper mounts and consumes
      // this, replaying its tour from step 1.
      requestTour(cfg.action.tourKey);
      return;
    }

    handleGuideClick(cfg.id);
  };

  const renderItemRow = (cfg: ChecklistItemConfig) => {
    const completed = isComplete(cfg.id);
    const animate = isRecentlyCompleted(cfg.id);
    const isTour = cfg.action.kind === "tour";
    // Completed tour items stay clickable so the user can replay their tour;
    // completed guide items are done and non-interactive as before.
    const interactive = !completed || isTour;
    return (
      <ItemRow
        key={cfg.id}
        type="button"
        $interactive={interactive}
        disabled={!interactive}
        onClick={() => handleItemClick(cfg)}
      >
        <ItemCircle $completed={completed} $animate={animate}>
          {completed && (
            <CheckIconWrapper $animate={animate}>
              <Icon name="check" size={14} color="white" />
            </CheckIconWrapper>
          )}
        </ItemCircle>
        <ItemLabel md semibold $completed={completed} $animate={animate}>
          {t(cfg.labelKey)}
        </ItemLabel>
        {!completed && (
          <Icon name="chevronRight" size={16} color={theme.colors.gr3} />
        )}
        {completed && isTour && <ReplayTag>{t("quickStart.replay")}</ReplayTag>}
      </ItemRow>
    );
  };

  const renderLockedRow = (cfg: ChecklistItemConfig) => (
    <ItemRow
      key={cfg.id}
      type="button"
      $interactive
      onClick={() => handleItemClick(cfg)}
    >
      <LockCircle>
        <Icon name="lock" size={16} color={theme.colors.gr4} />
      </LockCircle>
      <ItemLabel md semibold $completed={false} $animate={false}>
        {t(cfg.labelKey)}
      </ItemLabel>
      <UpgradeTag>{t("quickStart.lockedHint")}</UpgradeTag>
    </ItemRow>
  );

  return (
    <Wrapper ref={wrapperRef}>
      <Pill
        type="button"
        $isOpen={isExpanded}
        $justCompleted={anyRecentlyCompleted}
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <ProgressRing progress={displayedProgress} />
        <span>
          {/* The page name stays outside PillEyebrow so it survives on mobile:
              a pill reading just "3/6" on a phone is the flat global counter
              this ticket replaced. */}
          <PillEyebrow>{t("quickStart.pulse")} · </PillEyebrow>
          {currentPageLabel && <>{currentPageLabel} · </>}
          <RouletteNumber value={displayedCount} />/{totalCount}
        </span>
      </Pill>

      {isExpanded && (
        <Panel role="dialog" aria-label={t("quickStart.title") || ""}>
          <Flex alignItems="flex-start" justifyContent="space-between" mb={4}>
            <Heading lg bold color={theme.colors.gr1}>
              {t("quickStart.title")}
            </Heading>
            <CloseIconButton
              type="button"
              aria-label={t("quickStart.close") || ""}
              onClick={() => setIsExpanded(false)}
            >
              <Icon name="close" size={18} color={theme.colors.gr3} />
            </CloseIconButton>
          </Flex>

          <Flex alignItems="center" justifyContent="space-between" mb={1}>
            <Text sm color={theme.colors.gr3}>
              <Trans
                i18nKey="pages:quickStart.doneCount"
                values={{ current: displayedCount, total: totalCount }}
                components={{
                  highlight: (
                    <Text as="span" sm semibold color={theme.colors.gr1} />
                  ),
                }}
              />
            </Text>
            <Text sm color={theme.colors.gr3}>
              {Math.round(displayedProgress * 100)}%
            </Text>
          </Flex>
          <Box mb={3}>
            <ProgressBarTrack>
              <ProgressBarFill $progress={displayedProgress} />
            </ProgressBarTrack>
          </Box>

          <Box mb={2}>
            {hasOnThisPage && (
              <>
                <SectionLabel semibold color={theme.colors.gr3}>
                  {t("quickStart.onThisPage")}
                </SectionLabel>
                {onThisPageItems.map(renderItemRow)}
                {onThisPageLockedItems.map(renderLockedRow)}
              </>
            )}

            {exploreNextItems.length > 0 && (
              <>
                {hasOnThisPage && (
                  <SectionLabel semibold color={theme.colors.gr3}>
                    {t("quickStart.exploreNext")}
                  </SectionLabel>
                )}
                {exploreNextItems.map(renderItemRow)}
              </>
            )}

            {otherLockedItems.length > 0 && (
              <>
                <SectionLabel semibold color={theme.colors.gr3}>
                  {t("quickStart.unlockTitle")}
                </SectionLabel>
                {otherLockedItems.map(renderLockedRow)}
              </>
            )}
          </Box>

          <Footer>
            <FooterLink type="button" onClick={dismiss}>
              {t("quickStart.dontShowAgain")}
            </FooterLink>
          </Footer>
        </Panel>
      )}

      {/* The checklist lives in the Header, above page content, so a locked-item
          click toggles the GLOBAL ModalProvider — but every page's own
          UpgradeSubscriptionModal is scoped to a nested ModalProvider that
          shadows the global one. Mount our own instance here (bound to the
          global context) so the upgrade modal actually opens. Only needed when
          there are locked items to click — or when we already opened it, so a
          purchase landing on the websocket can't unmount it mid-flow. */}
      {(lockedItems.length > 0 || upgradeModalUsed) && (
        <UpgradeSubscriptionModal type={upgradeSubscriptionModal?.type} />
      )}
    </Wrapper>
  );
};

export default QuickStartChecklist;
